import { useSyncExternalStore } from 'react';

import { type DynamicWidgetModel } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_schema';

/*
 * Registry of the widget models currently rendered in the transcript, plus the
 * inspector's selection. It is a module store rather than React state because
 * the widgets and the debug sidebar are siblings under the app layout, and the
 * transcript must not re-render when a debug selection changes.
 *
 * Widgets register only while the debug sidebar is open (see
 * `DynamicWidgetInspectorRegistration`), so this stays empty for every user
 * who is not an internal admin with the panel open.
 */

export type DynamicWidgetInspectorSelection = {
    readonly widgetId: string;
    readonly bindingId: string | null;
};

export type DynamicWidgetInspectorState = {
    readonly models: ReadonlyArray<DynamicWidgetModel>;
    readonly selection: DynamicWidgetInspectorSelection | null;
};

type RegisteredWidget = {
    readonly model: DynamicWidgetModel;
    readonly fingerprint: string;
    readonly renderCount: number;
};

const registeredWidgets = new Map<string, RegisteredWidget>();
const listeners = new Set<() => void>();

const EMPTY_STATE: DynamicWidgetInspectorState = {
    models: [],
    selection: null,
};

let state: DynamicWidgetInspectorState = EMPTY_STATE;

function publish(selection: DynamicWidgetInspectorSelection | null): void {
    state = {
        models: [...registeredWidgets.values()].map(
            (registered) => registered.model,
        ),
        selection,
    };
    for (const listener of listeners) {
        listener();
    }
}

function isSameSelection(
    left: DynamicWidgetInspectorSelection | null,
    right: DynamicWidgetInspectorSelection | null,
): boolean {
    if (left === null || right === null) {
        return left === right;
    }
    return (
        left.widgetId === right.widgetId && left.bindingId === right.bindingId
    );
}

export const dynamicWidgetInspectorStore = {
    /**
     * Registers a rendered model and returns the matching unregister. A widget
     * can be mounted twice (streaming and settled projections of the same
     * entry), so registrations are reference counted.
     */
    register(model: DynamicWidgetModel): () => void {
        const fingerprint = JSON.stringify(model);
        const existing = registeredWidgets.get(model.widgetId);
        registeredWidgets.set(model.widgetId, {
            model,
            fingerprint,
            renderCount: (existing?.renderCount ?? 0) + 1,
        });
        if (existing === undefined || existing.fingerprint !== fingerprint) {
            publish(state.selection);
        }
        return () => {
            const current = registeredWidgets.get(model.widgetId);
            if (current === undefined) {
                return;
            }
            if (current.renderCount > 1) {
                registeredWidgets.set(model.widgetId, {
                    ...current,
                    renderCount: current.renderCount - 1,
                });
                return;
            }
            registeredWidgets.delete(model.widgetId);
            publish(
                state.selection?.widgetId === model.widgetId
                    ? null
                    : state.selection,
            );
        };
    },

    select(widgetId: string, bindingId: string | null): void {
        const selection = { widgetId, bindingId };
        if (isSameSelection(state.selection, selection)) {
            return;
        }
        publish(selection);
    },

    getState(): DynamicWidgetInspectorState {
        return state;
    },

    subscribe(listener: () => void): () => void {
        listeners.add(listener);
        return () => {
            listeners.delete(listener);
        };
    },

    /** Test helper: drops every registration and the selection. */
    reset(): void {
        registeredWidgets.clear();
        state = EMPTY_STATE;
        for (const listener of listeners) {
            listener();
        }
    },
};

export function useDynamicWidgetInspectorState(): DynamicWidgetInspectorState {
    return useSyncExternalStore(
        dynamicWidgetInspectorStore.subscribe,
        dynamicWidgetInspectorStore.getState,
        dynamicWidgetInspectorStore.getState,
    );
}
